import { useEffect, useRef, useState } from 'react'

/**
 * The login banner's background video (`tradeInConfig.banner.video`): silent, looping, stacked over
 * the banner images and under the scrim. It stays transparent until it is actually playing, and takes
 * itself out if it errors or the browser refuses to autoplay it (iOS Low Power Mode does), so the
 * images underneath are always the fallback — never a blank panel or a stray play button.
 */
export function BannerVideo({ url, position }: { url: string; position?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)

  // Start playback ourselves rather than via `autoPlay`: the play() promise is the only place a
  // blocked autoplay is reported. An AbortError just means the element went away mid-start.
  useEffect(() => {
    videoRef.current?.play().catch((error: DOMException) => {
      if (error.name !== 'AbortError') setFailed(true)
    })
  }, [])

  if (failed) return null

  return (
    <video
      ref={videoRef}
      className={`fp-banner-video${playing ? ' is-playing' : ''}`}
      src={url}
      style={position ? { objectPosition: position } : undefined}
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      onPlaying={() => setPlaying(true)}
      onError={() => setFailed(true)}
    />
  )
}
